<template lang="pug">
.container
  .rack-badges
    ProductBadges(v-if="badgeMeta" :meta="badgeMeta" :parentMeta="groupMeta" theme="group-child")
  .rack-section(v-if="rackBundleOptions.length")
    .rack-heading RACK
    .bundle
      BundleOption(
        v-for="(child, i) in rackBundleOptions"
        :key="rackBuyRequest.productId + '' + i"
        :buyRequest="child"
        :bundleBuyRequest="rackBuyRequest"
        :rackBuyRequest="rackBuyRequest"
        :hiddenAttributes="rackWidthHiddenAttributes"
      )
  Accordion.accordian-styles(
    v-for="(unit, i) in unitAccordions"
    :modelValue="openUnitIndex === i"
    :key="unit.key"
    :heading="unit.heading"
    open-icon="arrow-up"
    closed-icon="arrow-down"
    :theme="'custom-rhpa-styles'"
  )
    template(v-if="unit.id !== null")
      button.link.delete-unit(type="button" @click="displayModal(unit.id)") {{ __("Delete Unit") }}
      Modal(v-if="unitPendingDeletionId === unit.id" @close="unitPendingDeletionId = null")
        h2.center {{ __("Are you sure?")}}
        div.center.delete-info {{ __("This action can't be undone.")}}
        RogueButton.delete-btn(width="full" color="black" @click="removeUnit(unit.id)")
          div {{ __('Delete Unit') }}
    .bundle
      Configuration(
        v-if="unit.id === null && rackMainOptionRow"
        :buyRequest="rackMainOptionRow"
        :bundleBuyRequest="rackBuyRequest"
        :hiddenAttributes="rackLevelHiddenAttributes"
        :hideFiveForFive="true"
      )
      BundleOption(
        v-for="(child, j) in unit.options"
        :key="unit.key + j"
        :buyRequest="child"
        :bundleBuyRequest="unit.bundleBuyRequest"
        :rackBuyRequest="rackBuyRequest"
        :hiddenAttributes="unit.hiddenAttributes"
      )
  .button-container
    template(v-if="units.length === 0")
      .left-wrapper
        RogueButton(width="full" color="black-outline" @click="() => addUnit('left')")
          .btn-content
            Icon.storage-icon.left-icon(name="rackbuilder/storage_left")
            div.add-text {{ __('ADD UNIT LEFT') }}
      .right-wrapper
        RogueButton(width="full" color="black-outline" @click="() => addUnit('right')")
          .btn-content
            Icon.storage-icon.right-icon(name="rackbuilder/storage_right")
            div.add-text {{ __('ADD UNIT RIGHT') }}
    template(v-else)
      .button-wrapper
        RogueButton(width="full" color="black-outline" @click="() => addUnit(defaultSide)")
          .btn-content
            Icon.storage-icon(:name="defaultSide === 'left' ? 'rackbuilder/storage_left' : 'rackbuilder/storage_right'")
            div.add-text {{ __('ADD UNIT') }}
  .below-units-section(v-if="belowUnitsOptions.length")
    .bundle
      BundleOption(
        v-for="(child, i) in belowUnitsOptions"
        :key="rackBuyRequest.productId + '-below-' + i"
        :buyRequest="child"
        :bundleBuyRequest="rackBuyRequest"
        :rackBuyRequest="rackBuyRequest"
      )
  .divider
</template>
<script lang="ts" setup>
import { computed, defineAsyncComponent, onMounted, onServerPrefetch, nextTick, ref, toRef } from 'vue'
import ProductBadges from './badges.vue'
import { useUnitAccordions } from './custom/useUnitAccordions'
import { getBuyRequestMeta } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import { HYBRID_PLATE_STORAGE_OPTION_TITLE, HYBRID_RACK_LEVEL_ATTRIBUTE_CODES } from '@/js/pages/product/rackbuilder/products/rig/hybrid/hybridConstants'
import { getHybridBundleSimple, getHybridUnitLabel, getHybridWidth, inheritRearCrossmember, isHybridRackLevelOption } from '@/js/pages/product/rackbuilder/products/rig/hybrid/hybridUtils'
import type { BuyRequest } from '@/js/services/RogueTypes'

const BundleOption = defineAsyncComponent(() => import('@/js/components/rhpa/bundle-option.vue'))

const props = defineProps<{ buyRequest: BuyRequest }>()
const buyRequestRef = toRef(props, 'buyRequest')

const rogue = useRogue()
const productStore = useProductStore()
// The unit template is this rig's own addon bundle - children[1] by construction - so a single-sku constant would pin Monster Lite to Monster's bundle.
const addonBundleSku = computed(() => {
  const addon = props.buyRequest.children?.[1]

  return addon ? (rogue.getProductById(addon.productId)?.sku || addon.sku) : undefined
})

// Only the unit template needs warming - the group is this page's own product, already loaded, and the family sku lists now span both lines, so preloading them would fetch the other line's whole catalog.
const preloadSkus = computed(() => addonBundleSku.value ? [addonBundleSku.value] : [])

const rackBuyRequest = computed(() => props.buyRequest.children?.[0])
// getProductById declares a non-optional Product but indexes a store, so an absent rack child must be guarded here rather than handed a fabricated id.
const rackBundleProduct = computed(() => {
  const id = rackBuyRequest.value?.productId

  return id ? rogue.getProductById(id) : undefined
})
// The group's meta only carries a lead time every accessory shares, so badges come from the rack bundle, as on Cave.
const groupMeta = computed(() => getBuyRequestMeta(props.buyRequest, rogue))
const badgeMeta = computed(() => getBuyRequestMeta(rackBuyRequest.value ?? props.buyRequest, rogue))

const rackOptionRows = computed(() => (rackBuyRequest.value?.children ?? []).filter(child => child.metadata?.bundleOptionId))

// The rack itself and rack-wide accessories stay above the accordions. Everything else on the rack bundle describes the first unit specifically, so it moves into Unit 1 alongside the identical rows every added unit gets. Matching on the rack-level titles rather than the unit-level ones means new per-unit options land in the accordion by default.
function bundleOptionFor(child: BuyRequest) {
  return rackBundleProduct.value?.bundleOptions?.find(opt => opt.id === child.metadata?.bundleOptionId)
}

// Placement only, at business request: plate storage stays rack-level everywhere else, so the cart's rack/unit split via isHybridRackLevelOption is deliberately untouched.
function isBelowUnitsOption(child: BuyRequest): boolean {
  return (bundleOptionFor(child)?.title ?? '').toLowerCase().includes(HYBRID_PLATE_STORAGE_OPTION_TITLE)
}

const rackBundleOptions = computed(() => rackOptionRows.value.filter(child => isHybridRackLevelOption(bundleOptionFor(child)) && !isBelowUnitsOption(child)))
const belowUnitsOptions = computed(() => rackOptionRows.value.filter(isBelowUnitsOption))
const unitOneOptions = computed(() => rackOptionRows.value.filter(child => !isHybridRackLevelOption(bundleOptionFor(child))))

function unitBundleOptions(unit: { child: BuyRequest }): BuyRequest[] {
  return (unit.child.children ?? []).filter(child => child.metadata?.bundleOptionId)
}

function selectDefaultSide(): 'left' | 'right' {
  return props.buyRequest.children
    ?.map(child => child.metadata?.side as 'left' | 'right')
    .filter(side => ['left', 'right'].includes(side))[0] || 'left'
}

const defaultSide = ref<'left' | 'right'>(selectDefaultSide())

// Aliased so the template binds to the addUnit/removeUnit wrappers below - the composable's own pair leaves the open accordion where it was.
const {
  units,
  unitPendingDeletionId,
  addUnit: addUnitToRack,
  displayModal,
  removeUnit: removeUnitFromRack,
} = useUnitAccordions(buyRequestRef, {
  isUnit      : child => !!child.metadata?.side,
  onBeforeAdd : (newUnit, currentUnitCount, side) => {
    if (currentUnitCount === 0 && side) {
      defaultSide.value = side
    }

    inheritRearCrossmember(rogue, rackBuyRequest.value, newUnit)
  },
})

// A unit's own Rig configurable repeats the rack's color, height and depth; hiddenAttributes both drops those rows and writes the rack's value into the unit, leaving Rack Width as the only attribute a unit chooses for itself.
const rackLevelHiddenAttributes = computed(() => {
  const attributes = getHybridBundleSimple(rogue, rackBuyRequest.value)?.attributes

  return Object.fromEntries(HYBRID_RACK_LEVEL_ATTRIBUTE_CODES
    .map(code => [code, attributes?.[code]])
    .filter(([, value]) => typeof value === 'string'))
})

// Unit 1's width is chosen inside its own accordion, so the rack section renders the same Rig configurable with the width row hidden - and fed back in, since each instance resolves the shared simple from its own copy of the configuration.
const rackWidthHiddenAttributes = computed(() => {
  const width = getHybridWidth(rogue, rackBuyRequest.value)

  return width ? { mass_width: width } : {}
})

// Width is the one attribute the rack section hands to the Unit 1 accordion, which needs the main option's own row to render it from.
const rackMainOptionRow = computed(() => rackOptionRows.value.find(child => bundleOptionFor(child)?.addonType === 'main'))

// Unit 1 is the central rack itself, which has no unit to delete and always leads; added units follow in the order they were chained on.
const unitAccordions = computed(() => [
  {
    key              : 'unit-main',
    heading          : getHybridUnitLabel(rogue, rackBuyRequest.value, 0),
    options          : unitOneOptions.value,
    bundleBuyRequest : rackBuyRequest.value,
    hiddenAttributes : {},
    id               : null as number | null,
  },
  ...units.value.map((unit, index) => ({
    key              : `unit-${unit.id}`,
    heading          : getHybridUnitLabel(rogue, unit.child, index + 1),
    options          : unitBundleOptions(unit),
    bundleBuyRequest : unit.child,
    hiddenAttributes : rackLevelHiddenAttributes.value,
    id               : unit.id as number | null,
  })),
])

// Tracked here rather than via the composable's own index, which counts added units only and so is off by one against a list that leads with Unit 1.
const openUnitIndex = ref(0)

function addUnit(side?: 'left' | 'right') {
  // Captured before the add: the new unit lands last, and units won't have refreshed yet.
  const newUnitIndex = units.value.length + 1

  addUnitToRack(side)
  openUnitIndex.value = newUnitIndex
}

function removeUnit(id: number) {
  removeUnitFromRack(id)
  openUnitIndex.value = 0
}

onServerPrefetch(async () => {
  await productStore.loadAll(preloadSkus.value, rogue)
})

onMounted(async () => {
  await nextTick()

  if (!addonBundleSku.value || !productStore.products[addonBundleSku.value]) {
    await productStore.loadAll(preloadSkus.value, rogue)
  }
})
</script>
<style lang="scss">
.accordion {
  border: none !important;

  .heading {
    border: none !important;
    border-top: $border-width-thin solid $color-gray-light !important;
    padding-left: 0 !important;
  }

  .content {
    border-top: none !important;
    border-bottom: none !important;
  }

  button.link:focus, button.a11y:focus  {
    outline: none;
  }
}
</style>
<style lang="scss" scoped>
.container {
  font-size: $size-xs;
  margin-bottom: 10px;

  .rack-badges :deep(.badges) {
    padding-bottom: $space-xs;
  }

  .rack-section {
    border-top: $border-width-thin solid $color-gray-light;
    padding: $space-s 0;
  }

  .rack-heading {
    font-size: $size-xs;
    font-family: $font-condensed;
    font-weight: $weight-bold;
    text-transform: uppercase;
    line-height: $line-height-s;
    margin-bottom: $space-s;
  }

  .button-container {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 25px;

    .left-wrapper, .right-wrapper, .button-wrapper {
      margin-top: 5px;
      width: 100%;
      height: 100%;
    }

    .btn-content {
      display: flex;
      justify-content: center;
      align-items: center;
      height: $size-xs;
      font-size: $size-xs;
      font-family: $font-condensed;
      font-weight: $weight-bold;
      text-transform: uppercase;
      line-height: $line-height-s;

      .storage-icon {
        height: $size-l;
        width: $size-l;
        margin-top: 5px;
      }

      .add-text {
        margin-left: 4px;
      }
    }
  }

  .below-units-section {
    padding-bottom: $space-s;
  }

  .divider {
    height: $border-width-thin;
    background-color: $color-gray;
    margin: $space-xxs 0 $size-s;
  }

  .accordion.accordian-styles {
    .content {
      padding-left: 0;
      padding-top: 0;
    }

    .delete-unit {
      display: block;
      width: 58px;
      height: $size-xxs;
      color: $color-blue;
      font-size: $size-xxs;
      font-family: $font-condensed;
      font-weight: $weight-normal;
      text-decoration: underline;
      line-height: $line-height-s;
      cursor: pointer;
      margin: 5px 0 10px 0;
    }
  }

  .delete-info {
    margin: 25px 0;
    font-size: $size-xs;
    font-family: $font-condensed;
    text-align: center;
    line-height: $line-height-l;
  }

}
</style>
